<template>
  <div class="ship-panels">
    <div class="ship-panel">
      <h6>{{ $t("messenger.ship.examples.track_title") }}</h6>
      <div class="ship-demo">
        <div class="mp-row">
          <PageAvatar :page="samplePage"
                      size="sm" />
          <div class="mp-bubble">
            <div class="mp-text">{{ $t("messenger.ship.examples.sample_bubble") }}</div>
            <span class="mp-btn">{{ $t("messenger.ship.examples.track_button") }}</span>
          </div>
        </div>
      </div>
      <p class="ship-panel-bold">{{ $t("messenger.ship.examples.auto_bold") }}</p>
      <ul class="ship-panel-list">
        <li>{{ $t("messenger.ship.examples.benefit_1") }}</li>
        <li>{{ $t("messenger.ship.examples.benefit_2") }}</li>
        <li>{{ $t("messenger.ship.examples.benefit_3") }}</li>
      </ul>
    </div>

    <div class="ship-panel">
      <h6>{{ $t("messenger.ship.examples.ship_title") }}</h6>
      <div class="ship-demo ship-chat">
        <div class="msg --in --last-in-group">
          <span class="cu-avatar"
                aria-hidden="true">{{ customerLetter }}</span>
          <div class="bubble">{{ $t("messenger.ship.examples.chat_incoming") }}</div>
        </div>
        <div class="ship-admin-label">{{ $t("messenger.ship.examples.admin_label") }}</div>
        <div class="msg --out">
          <div class="bubble --mono">{{ $t("messenger.ship.examples.order_mono") }}</div>
        </div>
        <div class="ship-order-card">
          <div class="ship-order-head">
            <span>{{ $t("messenger.ship.examples.order_head") }}</span>
            <span class="badge badge-pill status-badge --success pill">{{ $t("messenger.ship.examples.order_status") }}</span>
          </div>
          <div class="ship-order-name">{{ $t("messenger.ship.examples.order_name") }}</div>
          <div class="ship-order-sub">{{ $t("messenger.ship.examples.order_sub") }}</div>
        </div>
        <p class="ship-card-note">{{ $t("messenger.ship.examples.order_note") }}</p>
      </div>
      <hr class="ship-panel-div" />
      <p class="ship-panel-bold"
         v-html="nw('pitch_bold')"></p>
      <p class="ship-panel-sub"
         v-html="nw('pitch_sub')"></p>
      <p class="ship-panel-how"
         v-html="nw('pitch_how')"></p>
      <p class="ship-panel-alt"
         v-html="nw('pitch_alt')"></p>
    </div>
  </div>
</template>

<script>
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { escapeHtml } from "@/components/derived/messenger/format";

// The two example panels on the เพจ Facebook card: what the customer sees and what the shop gets
export default {
  name: "FbExamples",
  components: { PageAvatar },
  computed: {
    samplePage() {
      return { pageId: "sample", name: this.$t("messenger.ship.examples.sample_page"), pictureUrl: null };
    },
    customerLetter() {
      return String(this.$t("messenger.ship.examples.order_name")).replace(/^คุณ/, "").trim().charAt(0);
    },
  },
  methods: {
    // Phrase by phrase (split at the spaces) so a line wraps between phrases, never inside one
    nw(key) {
      return String(this.$t("messenger.ship.examples." + key))
        .split(" ")
        .filter(Boolean)
        .map((phrase) => '<span class="nw">' + escapeHtml(phrase) + "</span>")
        .join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
.ship-panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 767.98px) {
  .ship-panels {
    grid-template-columns: 1fr;
  }
}

.ship-panel {
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  padding: 1rem;
  background: #fafafa;
  min-width: 0;

  h6 {
    font-size: 1rem;
    font-weight: 700;
    color: #333;
    margin: 0 0 0.75rem;
  }
}

.ship-demo {
  background: #fff;
  border-radius: 10px;
  padding: 0.9rem;
}

.mp-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.mp-bubble {
  background: #f0f0f0;
  border-radius: 18px;
  max-width: 290px;
  overflow: hidden;
  color: #1c1e21;
  min-width: 0;
}

.mp-text {
  padding: 0.6rem 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9375rem;
  line-height: 1.45;
}

.mp-btn {
  display: block;
  border-top: 1px solid #dcdcdc;
  padding: 0.55rem 0.9rem;
  text-align: center;
  color: #1c75bb;
  font-weight: 700;
  font-size: 0.9375rem;
}

.ship-panel-bold {
  font-weight: 700;
  color: #333;
  margin: 0.8rem 0 0.35rem;
  font-size: 0.9375rem;
}

.ship-panel-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: #646464;
  font-size: 0.9375rem;
}

.ship-panel-sub {
  color: #646464;
  font-size: 0.9375rem;
  margin: 0;
}

.ship-panel-how {
  color: #333;
  font-size: 0.9375rem;
  margin: 0.6rem 0 0;
}

.ship-panel-alt {
  color: #646464;
  font-size: 0.8125rem;
  margin: 0.2rem 0 0;
}

.ship-chat {
  display: flex;
  flex-direction: column;

  .msg .bubble {
    max-width: 90%;
  }

  .bubble.--mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.85rem;
    text-align: left;
  }
}

.msg {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  margin: 0.2rem 0;

  &.--out {
    justify-content: flex-end;
  }

  .bubble {
    max-width: 68%;
    padding: 0.5rem 0.85rem;
    border-radius: 18px;
    font-size: 0.9375rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &.--in .bubble {
    background: #f0f0f0;
    color: #1c1e21;
  }

  &.--out .bubble {
    background: #1c75bb;
    color: #fff;
  }
}

.cu-avatar {
  width: 28px;
  height: 28px;
  font-size: 0.8rem;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  line-height: 1;
  background: #8e6cc9;
  user-select: none;
}

.ship-admin-label {
  align-self: flex-end;
  font-size: 0.8125rem;
  color: #767676;
  margin: 0.55rem 0.2rem 0.15rem;
}

.ship-order-card {
  margin-top: 0.7rem;
  background: #f4f8fb;
  border: 1px solid #dde6ee;
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
}

.ship-order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-weight: 700;
  color: #333;
  font-size: 0.9375rem;
}

.ship-order-name {
  color: #333;
  margin-top: 0.25rem;
  font-size: 0.9375rem;
}

.ship-order-sub {
  color: #646464;
  font-size: 0.875rem;
  margin-top: 0.1rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 10rem;
  font-size: 0.8125rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-badge {
  &.--success {
    background-color: rgba($brand-green-mid, 0.1);
    color: $brand-green-mid;
  }
}

.ship-panel-div {
  border: 0;
  border-top: 1px solid #e4e6eb;
  margin: 0.9rem 0;
}

.ship-card-note {
  font-size: 0.8125rem;
  color: #646464;
  margin: 0.5rem 0 0;
  text-align: center;
}

body:not(.--dark) .status-badge.--success {
  color: #1a6e2e;
}

body:not(.--dark) .mp-btn {
  color: #155f99;
}

body.--dark {
  .ship-panel {
    background: #4b4b4b;
    border-color: #555;

    h6,
    .ship-panel-bold {
      color: lightgray;
    }
  }

  .ship-panel-list {
    color: #bbb;
  }

  .ship-panel-sub,
  .ship-card-note {
    color: #bbb;
  }

  .ship-panel-how {
    color: lightgray;
  }

  .ship-panel-alt {
    color: #999;
  }

  .ship-admin-label {
    color: #bbb;
  }

  .ship-panel-div {
    border-top-color: #555;
  }

  .ship-demo {
    background: #373737;
  }

  .mp-bubble,
  .msg.--in .bubble {
    background: #4b4b4b;
    color: lightgray;
  }

  .ship-order-card {
    background: #4b4b4b;
    border-color: #555;
  }

  .ship-order-head,
  .ship-order-name {
    color: lightgray;
  }

  .ship-order-sub {
    color: #bbb;
  }
}

@media (max-width: 767.98px) {
  .pill {
    font-size: 12px;
  }
}
</style>

<style lang="scss">
/* v-html phrases are outside the scoped attribute */
.ship-panels .nw {
  white-space: nowrap;
}
</style>
